import { STATIC_RECTANGLE_SIZE_CM } from "@pufimi/shared";

// The landing's single source of truth for STRUCTURED + ASSET data (ADR-0040).
// Prose lives in i18n (`landing` namespace, PL/EN/DE) so it localises; this file
// holds the things that don't translate: which images exist, where they live,
// whether they're a launch-blocking placeholder, and the product facts that feed
// JSON-LD. Swapping an MVP stand-in for a real asset is a one-field edit here —
// no component changes — which is the "automate, don't make me drop files" path:
// drop the real file in /public/landing, point `src` at it, flip `placeholder`.
//
// MVP REALITY (2026-06-07): there are no finished-pillow photos or customer
// reviews yet. So proof imagery is HONEST Stage renders (kind: "render",
// placeholder: true) captured from the production /shot harness, and reviews are
// flagged placeholders rendered with monogram avatars (no fabricated faces, no
// invented ratings). Everything marked `placeholder: true` MUST be replaced
// before public launch — `isPlaceholder()` drives a dev-only marker so it can't
// quietly ship as truth.

export type ImageKind = "render" | "photo";

export interface LandingImage {
  /** Path under /public (or an absolute URL once real assets land). */
  src: string;
  /** `landing`-namespace i18n key for alt text. Alt is voice, so it localises. */
  altKey: string;
  /** "render" = a Stage still standing in for product photography (honest MVP);
   *  "photo" = a real product photo. Swapping render→photo is just this field. */
  kind: ImageKind;
  /** A launch-blocking MVP stand-in. Surfaced in dev; never a silent ship. */
  placeholder: boolean;
  /** Intrinsic px — set on the <img> so the box is reserved (no layout shift). */
  width: number;
  height: number;
}

export interface CraftStep {
  /** React key + i18n key segment: landing:craft.steps.<id>.{title,body}. */
  id: string;
  image: LandingImage;
}

export interface Testimonial {
  /** React key + i18n key segment: landing:promise.voices.<id>.{quote,name,pet}. */
  id: string;
  /** A real pet photo, once we have permission. Absent → a monogram avatar
   *  (the MVP default: tasteful, clearly not a stock face). */
  avatar?: LandingImage;
  placeholder: boolean;
}

export interface LandingContent {
  hero: { poster: LandingImage };
  proof: { before: LandingImage; after: LandingImage };
  craft: { steps: CraftStep[]; macro: LandingImage };
  /** In-scale / lifestyle shot (pillow in a hand / on a sofa) — Baymard: a
   *  floating product reads "cheap", an in-context one converts. */
  lifestyle: LandingImage;
  /** The subtle memorial thread — one quiet, nostalgic image. */
  memorial: LandingImage;
  /** The second product line: the 3D print ("obraz 3D"). One section only. */
  print3d: LandingImage;
  /** Real customer photos (UGC). EMPTY at launch — filled from first orders,
   *  never fabricated ([PRODUCT.md] social-proof rule). */
  ugc: { photos: LandingImage[] };
  promise: { voices: Testimonial[] };
  trust: {
    /** i18n key segments: landing:trust.assurances.<id>.{title,body}; icon mapped
     *  in the component (keeps this file data-only). */
    assuranceIds: readonly string[];
    /** Producer-truth dimensions, kept in lockstep with @pufimi/shared. */
    sizesCm: readonly (readonly [number, number])[];
  };
  /** No fabricated price in the MVP. JSON-LD emits the Offer price ONLY when this
   *  is a real number; until then it ships Product + brand + image with no price. */
  pricing: { price: number | null; currency: "PLN" | "EUR" };
}

// The committed sample pet that the /shot harness and the mock fixture already
// use (public/pillow-sample-pet.jpg). It's the honest "before" for the proof
// comparison — a real photo of a real animal — until customer photos exist.
const SAMPLE_PET_PHOTO = "/pillow-sample-pet.jpg";

export const LANDING_CONTENT: LandingContent = {
  hero: {
    poster: {
      // Captured by `pnpm shot` (three-quarter, reveal=1) → public/landing/. The
      // crawlable, no-WebGL, reduced-motion still that the live Stage fades over.
      // (JPEG today — the web container has no webp encoder; re-encode to webp/avif
      // when the imagery is swapped for real photos.)
      src: "/landing/hero.jpg",
      altKey: "hero.posterAlt",
      kind: "render",
      placeholder: true,
      width: 1280,
      height: 960,
    },
  },
  proof: {
    before: {
      src: SAMPLE_PET_PHOTO,
      altKey: "proof.beforeAlt",
      kind: "photo",
      placeholder: false,
      width: 1080,
      height: 1080,
    },
    after: {
      src: "/landing/pillow-front.jpg",
      altKey: "proof.afterAlt",
      kind: "render",
      placeholder: true,
      width: 1280,
      height: 960,
    },
  },
  craft: {
    // The three making beats are the Stage's OWN reveal progress, captured at
    // t≈0.15 / 0.5 / 1.0 — honest, because it's literally how the pillow is built
    // (outline → sewn → stuffed), not a fabricated process shot.
    steps: [
      {
        id: "outline",
        image: {
          src: "/landing/make-1-outline.jpg",
          altKey: "craft.steps.outline.alt",
          kind: "render",
          placeholder: true,
          width: 1280,
          height: 960,
        },
      },
      {
        id: "sewn",
        image: {
          src: "/landing/make-2-sewn.jpg",
          altKey: "craft.steps.sewn.alt",
          kind: "render",
          placeholder: true,
          width: 1280,
          height: 960,
        },
      },
      {
        id: "stuffed",
        image: {
          src: "/landing/make-3-stuffed.jpg",
          altKey: "craft.steps.stuffed.alt",
          kind: "render",
          placeholder: true,
          width: 1280,
          height: 960,
        },
      },
    ],
    // The macro craft shot (weave / seam / fill in real light). MUST become a REAL
    // photo — a render reads as AI here. Placeholder until the first fabric swatch
    // or pillow exists (see the Outline photo brief, "poz. 3").
    macro: {
      src: "/landing/craft-macro.jpg",
      altKey: "craft.macroAlt",
      kind: "photo",
      placeholder: true,
      width: 1600,
      height: 1067,
    },
  },
  lifestyle: {
    src: "/landing/lifestyle-in-hand.jpg",
    altKey: "inscale.imageAlt",
    kind: "photo",
    placeholder: true,
    width: 1600,
    height: 1067,
  },
  memorial: {
    src: "/landing/memorial.jpg",
    altKey: "memorial.imageAlt",
    kind: "photo",
    placeholder: true,
    width: 1280,
    height: 960,
  },
  print3d: {
    src: "/landing/print-3d.jpg",
    altKey: "print3d.imageAlt",
    kind: "render",
    placeholder: true,
    width: 1200,
    height: 1200,
  },
  // Empty at launch: the UGC gallery fills from real customer photos, never faked.
  ugc: { photos: [] },
  promise: {
    // Placeholder reviews for the MVP (Leon's call: real-feeling page to gather
    // feedback, swapped for genuine quotes before public launch). No avatars →
    // monogram fallback; no invented star count / aggregate rating anywhere.
    voices: [
      { id: "v1", placeholder: true },
      { id: "v2", placeholder: true },
      { id: "v3", placeholder: true },
    ],
  },
  trust: {
    assuranceIds: ["preview", "made", "shipping", "guarantee"],
    sizesCm: Object.values(STATIC_RECTANGLE_SIZE_CM),
  },
  pricing: { price: null, currency: "PLN" },
};

/** Whether any launch-blocking stand-in is still in the manifest. Drives the
 *  dev-only "swap before launch" marker; also handy for a future CI guard. */
export function hasPlaceholders(content: LandingContent = LANDING_CONTENT): boolean {
  return (
    content.hero.poster.placeholder ||
    content.proof.before.placeholder ||
    content.proof.after.placeholder ||
    content.craft.steps.some((s) => s.image.placeholder) ||
    content.craft.macro.placeholder ||
    content.lifestyle.placeholder ||
    content.memorial.placeholder ||
    content.print3d.placeholder ||
    content.ugc.photos.some((p) => p.placeholder) ||
    content.promise.voices.some((v) => v.placeholder)
  );
}
